import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useLocation, useNavigate } from "react-router-dom";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ModalCloseX from "../components/common/ModalCloseX";
import DateRangeErrorBanner from "../components/common/DateRangeErrorBanner";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import "./ReportsSectionCompact.css";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import {
  getProfitabilityEstimateContext,
  getProfitabilityEstimateConfig,
  getProfitabilityEstimateRevenue,
  getPayrollSummary,
  runProfitabilityEstimate,
  saveProfitabilityEstimateConfig,
} from "../api/reports";
import { useAuth } from "../context/AuthContext";
import useTableSort from "../hooks/useTableSort";
import {
  PROFITABILITY_CATEGORIES,
  PROFITABILITY_TOOLTIPS,
} from "../constants/profitabilityEstimate";
import { REPORT_HELP } from "../constants/reportHelpContent";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { getReportsHomePathForRoute } from "../utils/reportRouteContext";

const WAGES_ITEM_KEY = "gifi_9060";
const INVENTORY_ESTIMATE_ITEM_KEYS = new Set(["gifi_8320", "gifi_9130"]);
const DATE_FORMAT = "YYYY-MM-DD";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

const formatMoney = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "$0.00";
  const fixed = Math.abs(number).toFixed(2);
  return number < 0 ? `-$${fixed}` : `$${fixed}`;
};

const formatPercentValue = (value) => {
  const number = Number(value ?? 0);
  if (Number.isNaN(number)) return "0.00%";
  return `${number.toFixed(2)}%`;
};

const sumProrationFactors = (parts = []) =>
  parts.reduce((sum, part) => sum + Number(part?.factor || 0), 0);

const buildLabel = (code, title, fallback) => {
  const cleanCode = String(code || "").trim();
  const cleanTitle = String(title || "").trim();
  if (cleanCode && cleanTitle) {
    return `GIFI ${cleanCode} - ${cleanTitle}`;
  }
  if (cleanTitle) return cleanTitle;
  if (cleanCode) return cleanCode;
  return fallback || "";
};

const createLocalId = () =>
  `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const MODE_OPTIONS = [
  { key: "fixed", label: "$ / month" },
  { key: "percent", label: "% of revenue" },
];

const QUICK_PICK_OPTIONS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
];

const QUICK_PICK_BASE =
  "h-9 px-4 rounded-full text-sm font-semibold transition";
const QUICK_PICK_ACTIVE = "bg-rose-500 text-white";
const QUICK_PICK_INACTIVE =
  "border border-slate-300 text-slate-700 hover:border-slate-400";

const parseSalonDate = (value, timeZone) => {
  if (!value || !timeZone) return null;
  const parsed = dayjs.tz(value, DATE_FORMAT, timeZone);
  return parsed.isValid() ? parsed : null;
};

const buildQuickPickRange = ({ pick, timeZone, salonTodayIso }) => {
  if (!timeZone || !pick) return null;
  const base =
    parseSalonDate(salonTodayIso, timeZone) || dayjs().tz(timeZone);
  if (!base?.isValid?.()) return null;
  let start = base;
  let end = base;
  if (pick === "week") {
    start = base.startOf("isoWeek");
    end = base.endOf("isoWeek");
  } else if (pick === "month") {
    start = base.startOf("month");
    end = base.endOf("month");
  } else if (pick === "year") {
    start = base.startOf("year");
    end = base.endOf("year");
  }
  return {
    start: start.format(DATE_FORMAT),
    end: end.format(DATE_FORMAT),
  };
};

export default function ProfitabilityEstimateReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSalon, user, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.PROFITABILITY_ESTIMATE;
  const salonTimezone = useMemo(
    () =>
      currentSalon?.timezone ||
      currentSalon?.business?.timezone ||
      currentSalon?.business_timezone ||
      authSalonTimezone ||
      "UTC",
    [currentSalon, authSalonTimezone]
  );
  const salonId = useMemo(
    () => currentSalon?.id ?? user?.salon_id ?? null,
    [currentSalon, user]
  );

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [items, setItems] = useState([]);
  const [salonContext, setSalonContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [taxRatePercent, setTaxRatePercent] = useState("0.00");
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");
  const [formError, setFormError] = useState("");
  const [runResult, setRunResult] = useState(null);
  const [revenuePreview, setRevenuePreview] = useState(null);
  const [revenuePreviewLoading, setRevenuePreviewLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [wagesSourceError, setWagesSourceError] = useState("");
  const [wagesManualSnapshot, setWagesManualSnapshot] = useState(null);
  const [categorySortDir, setCategorySortDir] = useState(null);
  const [quickPick, setQuickPick] = useState(null);

  const effectiveTimezone = useMemo(
    () => salonContext?.timezone || salonTimezone || "UTC",
    [salonContext, salonTimezone]
  );
  const salonTodayIso = useMemo(() => {
    if (salonContext?.salon_today_iso) return salonContext.salon_today_iso;
    if (!effectiveTimezone) return "";
    const fallback = dayjs().tz(effectiveTimezone);
    return fallback.isValid() ? fallback.format(DATE_FORMAT) : "";
  }, [salonContext, effectiveTimezone]);
  const defaultRange = useMemo(() => {
    const base = parseSalonDate(salonTodayIso, effectiveTimezone);
    if (!base) return { start: "", end: "" };
    return {
      start: base.startOf("month").format(DATE_FORMAT),
      end: base.format(DATE_FORMAT),
    };
  }, [salonTodayIso, effectiveTimezone]);

  const categoryByKey = useMemo(() => {
    const map = new Map();
    PROFITABILITY_CATEGORIES.forEach((item) => {
      map.set(item.key, item);
    });
    return map;
  }, []);

  const isWagesItem = (item) =>
    item?.item_key === WAGES_ITEM_KEY && !item?.is_misc;
  const isPayrollSource = (item) =>
    isWagesItem(item) && item?.value_source === "payroll";

  useEffect(() => {
    if (!defaultRange.start || !defaultRange.end) return;
    setStartDate((prev) => prev || defaultRange.start);
    setEndDate((prev) => prev || defaultRange.end);
  }, [defaultRange.start, defaultRange.end]);

  useEffect(() => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
    } else {
      setDateRangeError("");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!salonId) return;
    let mounted = true;
    const loadContext = async () => {
      setContextLoading(true);
      try {
        const data = await getProfitabilityEstimateContext({ salonId });
        if (!mounted) return;
        setSalonContext(data || null);
      } catch (error) {
        if (!mounted) return;
        setSalonContext(null);
      } finally {
        if (mounted) setContextLoading(false);
      }
    };

    loadContext();
    return () => {
      mounted = false;
    };
  }, [salonId]);

  useEffect(() => {
    if (!salonId) return;
    let mounted = true;
    const loadConfig = async () => {
      setConfigLoading(true);
      setConfigError("");
      try {
        const data = await getProfitabilityEstimateConfig({ salonId });
        if (!mounted) return;
        let manualSnapshot = null;
        const normalized = (data.items || []).map((item, idx) => {
          const valueSource = item.value_source || "manual";
          const isPayroll =
            item.item_key === WAGES_ITEM_KEY && valueSource === "payroll";
          if (isPayroll) {
            manualSnapshot = {
              input_mode: item.input_mode,
              tax_exempt: item.tax_exempt,
            };
          }
          return {
            ...item,
            value:
              item.value === null || item.value === undefined
                ? ""
                : String(item.value),
            value_source: valueSource,
            input_mode: isPayroll ? "fixed" : item.input_mode,
            tax_exempt: isPayroll ? true : item.tax_exempt,
            localId: item.item_key || createLocalId() + `_m${idx}`,
            sort_order: Number.isFinite(Number(item.sort_order))
              ? Number(item.sort_order)
              : idx,
          };
        });
        setItems(normalized);
        setTaxRatePercent(String(data.tax_rate_percent || "0.00"));
        setWagesManualSnapshot(manualSnapshot);
        setIsDirty(false);
      } catch (error) {
        if (!mounted) return;
        setConfigError("Unable to load profitability estimate settings.");
      } finally {
        if (mounted) setConfigLoading(false);
      }
    };

    loadConfig();
    return () => {
      mounted = false;
    };
  }, [salonId]);

  const refreshRevenuePreview = useCallback(
    async (start, end) => {
      if (!salonId || !start || !end || isInvalidDateRange(start, end)) {
        setRevenuePreview(null);
        setRevenuePreviewLoading(false);
        return;
      }
      setRevenuePreviewLoading(true);
      try {
        const data = await getProfitabilityEstimateRevenue({
          salonId,
          start_date: start,
          end_date: end,
        });
        setRevenuePreview(data?.revenue_in_range ?? null);
      } catch (error) {
        setRevenuePreview(null);
      } finally {
        setRevenuePreviewLoading(false);
      }
    },
    [salonId]
  );

  useEffect(() => {
    refreshRevenuePreview(startDate, endDate);
  }, [startDate, endDate, refreshRevenuePreview]);

  useEffect(() => {
    if (runResult?.revenue_in_range) {
      setRevenuePreview(runResult.revenue_in_range);
    }
  }, [runResult]);

  useEffect(() => {
    if (!quickPick) return;
    const range = buildQuickPickRange({
      pick: quickPick,
      timeZone: effectiveTimezone,
      salonTodayIso,
    });
    if (!range) return;
    if (range.start !== startDate || range.end !== endDate) {
      setQuickPick(null);
    }
  }, [quickPick, effectiveTimezone, salonTodayIso, startDate, endDate]);

  const updateItem = (matcher, updates) => {
    setItems((prev) =>
      prev.map((item) => (matcher(item) ? { ...item, ...updates } : item))
    );
    setIsDirty(true);
    setSaveSuccess("");
    setFormError("");
  };

  const handleModeChange = (item, mode) => {
    if (!item) return;
    if (isWagesItem(item)) {
      setWagesSourceError("");
    }
    updateItem(
      (entry) => entry.localId === item.localId,
      { input_mode: mode }
    );
  };

  const handleValueChange = (item, value) => {
    if (isWagesItem(item)) {
      setWagesSourceError("");
    }
    updateItem((entry) => entry.localId === item.localId, { value });
  };

  const handleTaxExemptToggle = (item, nextValue) => {
    updateItem((entry) => entry.localId === item.localId, {
      tax_exempt: nextValue,
    });
  };

  const handleLabelChange = (item, value) => {
    updateItem((entry) => entry.localId === item.localId, { label: value });
  };

  const handleWagesSourceChange = (item, nextSource) => {
    if (!isWagesItem(item)) return;
    setWagesSourceError("");
    if (nextSource === "payroll") {
      setWagesManualSnapshot({
        input_mode: item.input_mode,
        tax_exempt: item.tax_exempt,
      });
      updateItem((entry) => entry.localId === item.localId, {
        value_source: "payroll",
        input_mode: "fixed",
        tax_exempt: true,
      });
      return;
    }
    const restored = wagesManualSnapshot || {};
    updateItem((entry) => entry.localId === item.localId, {
      value_source: "manual",
      input_mode: restored.input_mode ?? item.input_mode,
      tax_exempt: restored.tax_exempt ?? item.tax_exempt,
    });
  };

  const miscItems = useMemo(
    () => items.filter((item) => item.is_misc),
    [items]
  );

  const getCategorySortLabel = (item) => {
    if (item.is_misc) {
      return String(item.label || "").trim();
    }
    const category = categoryByKey.get(item.item_key);
    return (
      String(item.label || "").trim() ||
      buildLabel(category?.gifiCode, category?.gifiTitle, item.item_key)
    );
  };

  const sortedCategoryItems = useMemo(() => {
    const combined = items.map((item, index) => ({
      item,
      index,
      label: getCategorySortLabel(item),
    }));
    if (!categorySortDir) return combined;
    const direction = categorySortDir === "asc" ? 1 : -1;
    const sorted = [...combined].sort((a, b) => {
      const cmp = a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (cmp !== 0) return cmp * direction;
      return a.index - b.index;
    });
    return sorted;
  }, [items, categorySortDir, categoryByKey]);

  const toggleCategorySort = (sortKey) => {
    if (sortKey && sortKey !== "category") return;
    setCategorySortDir((prev) => {
      if (prev === "asc") return "desc";
      return "asc";
    });
  };

  const handleAddMisc = () => {
    if (miscItems.length >= 25) {
      setFormError("Limit misc lines to 25.");
      return;
    }
    const nextSort =
      miscItems.length === 0
        ? 1
        : Math.max(
          ...miscItems.map((item) => Number(item.sort_order) || 0)
        ) + 1;
    setItems((prev) => [
      ...prev,
      {
        localId: createLocalId(),
        is_misc: true,
        label: "",
        input_mode: "fixed",
        value: "",
        tax_exempt: false,
        sort_order: nextSort,
      },
    ]);
    setIsDirty(true);
    setSaveSuccess("");
    setFormError("");
  };

  const handleRemoveMisc = (item) => {
    setItems((prev) => prev.filter((entry) => entry.localId !== item.localId));
    setIsDirty(true);
    setSaveSuccess("");
  };

  const validateItems = (itemsToValidate = items) => {
    const miscCount = itemsToValidate.filter((item) => item.is_misc).length;
    if (miscCount > 25) {
      return "Limit misc lines to 25.";
    }
    for (const item of itemsToValidate) {
      const rawValue = Number(item.value ?? 0);
      if (!Number.isFinite(rawValue) || rawValue < 0) {
        return "Values must be 0 or greater.";
      }
      if (item.input_mode === "percent" && rawValue > 100) {
        return "Percent values must be 100 or less.";
      }
      if (item.is_misc) {
        const label = String(item.label || "").trim();
        if (!label) {
          return "Miscellaneous labels are required.";
        }
        if (label.length > 60) {
          return "Miscellaneous labels must be 60 characters or less.";
        }
      }
    }
    return "";
  };

  const buildPayloadItems = (itemsToSave = items) => {
    return itemsToSave.map((item) => {
      const isWages = isWagesItem(item);
      const valueSource =
        isWages && item.value_source === "payroll" ? "payroll" : "manual";
      return {
        item_key: item.item_key || null,
        label: item.is_misc ? String(item.label || "").trim() : null,
        input_mode: item.input_mode || "fixed",
        value: Number(item.value ?? 0),
        value_source: valueSource,
        tax_exempt: Boolean(item.tax_exempt),
        sort_order: Number.isFinite(Number(item.sort_order))
          ? Number(item.sort_order)
          : undefined,
        is_misc: Boolean(item.is_misc),
      };
    });
  };

  const saveConfig = async ({ silent = false, itemsOverride } = {}) => {
    const workingItems = itemsOverride || items;
    setFormError("");
    const validationMessage = validateItems(workingItems);
    if (validationMessage) {
      setFormError(validationMessage);
      return false;
    }
    if (!salonId) {
      setSaveError("Salon context required.");
      return false;
    }
    setSaveLoading(true);
    if (!silent) {
      setSaveError("");
      setSaveSuccess("");
    }
    try {
      const data = await saveProfitabilityEstimateConfig({
        salonId,
        items: buildPayloadItems(workingItems),
      });
      let manualSnapshot = wagesManualSnapshot;
      const normalized = (data.items || []).map((item, idx) => {
        const valueSource = item.value_source || "manual";
        const isPayroll =
          item.item_key === WAGES_ITEM_KEY && valueSource === "payroll";
        if (isPayroll && !manualSnapshot) {
          manualSnapshot = {
            input_mode: item.input_mode,
            tax_exempt: item.tax_exempt,
          };
        }
        return {
          ...item,
          value:
            item.value === null || item.value === undefined
              ? ""
              : String(item.value),
          value_source: valueSource,
          input_mode: isPayroll ? "fixed" : item.input_mode,
          tax_exempt: isPayroll ? true : item.tax_exempt,
          localId: item.item_key || createLocalId() + `_m${idx}`,
          sort_order: Number.isFinite(Number(item.sort_order))
            ? Number(item.sort_order)
            : idx,
        };
      });
      setItems(normalized);
      setTaxRatePercent(String(data.tax_rate_percent || "0.00"));
      setWagesManualSnapshot(manualSnapshot);
      setIsDirty(false);
      if (!silent) {
        setSaveSuccess("Saved.");
      }
      return true;
    } catch (error) {
      if (!silent) {
        setSaveError("Unable to save profitability settings.");
      }
      return false;
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSave = async () => {
    await saveConfig({ silent: false });
  };

  const handleQuickPick = (pickKey) => {
    const range = buildQuickPickRange({
      pick: pickKey,
      timeZone: effectiveTimezone,
      salonTodayIso,
    });
    if (!range) return;
    setQuickPick(pickKey);
    setStartDate(range.start);
    setEndDate(range.end);
    refreshRevenuePreview(range.start, range.end);
  };

  const handleRun = async () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setRunError("");
    if (!salonId) {
      setRunError("Salon context required.");
      return;
    }

    let nextItems = items;
    const wagesItem = items.find((item) => isWagesItem(item));
    const usingPayroll = wagesItem && wagesItem.value_source === "payroll";
    if (usingPayroll) {
      try {
        const summary = await getPayrollSummary({
          salonId,
          start_date: startDate,
          end_date: endDate,
        });
        if (summary?.warning) {
          const fallbackItems = items.map((item) => {
            if (!isWagesItem(item)) return item;
            const restored = wagesManualSnapshot || {};
            return {
              ...item,
              value_source: "manual",
              input_mode: restored.input_mode ?? item.input_mode,
              tax_exempt: restored.tax_exempt ?? item.tax_exempt,
            };
          });
          setWagesSourceError(summary.warning);
          setItems(fallbackItems);
          setIsDirty(true);
          nextItems = fallbackItems;
        } else {
          setWagesSourceError("");
        }
      } catch (error) {
        const detail = error?.response?.data?.detail;
        const message =
          detail === "PAYROLL_VIEW_PERMISSION_REQUIRED"
            ? "You don't have permission to view payroll. Please use manual wages input."
            : "Payroll is not configured. Please use manual wages input.";
        const fallbackItems = items.map((item) => {
          if (!isWagesItem(item)) return item;
          const restored = wagesManualSnapshot || {};
          return {
            ...item,
            value_source: "manual",
            input_mode: restored.input_mode ?? item.input_mode,
            tax_exempt: restored.tax_exempt ?? item.tax_exempt,
          };
        });
        setWagesSourceError(message);
        setItems(fallbackItems);
        setIsDirty(true);
        nextItems = fallbackItems;
      }
    }

    const needsSave = isDirty || nextItems !== items;
    const saved = needsSave
      ? await saveConfig({ silent: true, itemsOverride: nextItems })
      : true;
    if (!saved) return;

    setRunLoading(true);
    try {
      const data = await runProfitabilityEstimate({
        salonId,
        start_date: startDate,
        end_date: endDate,
      });
      setRunResult(data);
      if (data?.wages_source_error) {
        const fallbackItems = items.map((item) => {
          if (!isWagesItem(item)) return item;
          const restored = wagesManualSnapshot || {};
          return {
            ...item,
            value_source: "manual",
            input_mode: restored.input_mode ?? item.input_mode,
            tax_exempt: restored.tax_exempt ?? item.tax_exempt,
          };
        });
        setWagesSourceError(data.wages_source_error);
        setItems(fallbackItems);
        setIsDirty(true);
        await saveConfig({ silent: true, itemsOverride: fallbackItems });
      } else {
        setWagesSourceError("");
      }
    } catch (error) {
      setRunError("Unable to run profitability estimate.");
    } finally {
      setRunLoading(false);
    }
  };

  const computedByKey = useMemo(() => {
    const map = new Map();
    (runResult?.computed_lines || []).forEach((line) => {
      const key = line.item_key
        ? `key:${line.item_key}`
        : `misc:${Number(line.sort_order) || 0}`;
      map.set(key, line);
    });
    return map;
  }, [runResult]);

  const breakdownRows = runResult?.computed_lines || [];
  const breakdownColumns = useMemo(
    () => ({
      category: { type: "text", getValue: (row) => row?.label || "" },
      input_mode: { type: "text", getValue: (row) => row?.input_mode || "" },
      value: { type: "number", getValue: (row) => row?.value },
      tax_exempt: {
        type: "text",
        getValue: (row) => (row?.tax_exempt ? "Yes" : "No"),
      },
      pretax: { type: "number", getValue: (row) => row?.pretax },
      sales_tax: { type: "number", getValue: (row) => row?.sales_tax },
      total: { type: "number", getValue: (row) => row?.total },
      pct_of_revenue: {
        type: "percent",
        getValue: (row) => row?.pct_of_revenue,
      },
    }),
    []
  );
  const breakdownSort = useTableSort({
    rows: breakdownRows,
    columns: breakdownColumns,
    defaultSortBy: "total",
  });

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };

  const renderModeToggle = (item, disabled = false) => (
    <div className="inline-flex rounded-full border border-slate-300 overflow-hidden">
      {MODE_OPTIONS.map((option) => {
        const isActive = item.input_mode === option.key;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => {
              if (!disabled) handleModeChange(item, option.key);
            }}
            disabled={disabled}
            className={`px-3 py-1 text-xs md:text-sm font-semibold transition ${isActive
              ? "bg-rose-500 text-white"
              : "text-slate-600 hover:text-slate-900"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  const renderValueInput = (item, disabled = false, displayValue = null) => (
    <div className="relative">
      <input
        type="number"
        inputMode="decimal"
        min="0"
        max={item.input_mode === "percent" ? "100" : undefined}
        step="0.01"
        value={displayValue ?? item.value}
        onChange={(event) => handleValueChange(item, event.target.value)}
        disabled={disabled}
        className={`${FIELD_CLASS} pr-10 ${disabled ? "bg-slate-50" : ""}`}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs md:text-sm text-slate-400">
        {item.input_mode === "percent" ? "%" : "$"}
      </span>
    </div>
  );

  const getInventoryEstimateForItem = (item) => {
    if (!item?.item_key || !INVENTORY_ESTIMATE_ITEM_KEYS.has(item.item_key)) {
      return null;
    }
    return runResult?.inventory_estimates?.[item.item_key] || null;
  };

  const handleUseInventoryEstimate = (item) => {
    const estimate = getInventoryEstimateForItem(item);
    const total = Number(estimate?.total ?? 0);
    const factor = sumProrationFactors(runResult?.proration_parts || []);
    if (!Number.isFinite(total) || total < 0 || !factor) return;
    const monthlyEquivalent = total / factor;
    updateItem((entry) => entry.localId === item.localId, {
      input_mode: "fixed",
      value: monthlyEquivalent.toFixed(2),
      value_source: "manual",
    });
    setSaveSuccess("Inventory estimate copied. Save, then run the estimate again.");
  };

  const renderInventoryEstimateCell = (item) => {
    if (!item?.item_key || !INVENTORY_ESTIMATE_ITEM_KEYS.has(item.item_key)) {
      return <span className="text-slate-400">--</span>;
    }
    const estimate = getInventoryEstimateForItem(item);
    if (!estimate) {
      return <span className="text-xs text-slate-500">Run estimate</span>;
    }
    const total = Number(estimate.total ?? 0);
    const canUse = Number.isFinite(total) && total >= 0 && sumProrationFactors(runResult?.proration_parts || []) > 0;
    const missingCount = Number(estimate.missing_cost_item_count || 0);
    const missingNames = Array.isArray(estimate.missing_cost_item_names)
      ? estimate.missing_cost_item_names
      : [];
    const missingText = missingCount
      ? `${missingCount} inventory item${missingCount === 1 ? "" : "s"} missing cost per unit${missingNames.length ? `: ${missingNames.join(", ")}` : ""}.`
      : "";
    return (
      <div className="flex min-w-[170px] flex-col gap-1">
        <div className={total < 0 ? "font-semibold text-amber-700" : "font-semibold text-slate-900"}>
          {formatMoney(estimate.total)}
        </div>
        <div className="text-xs text-slate-500">
          Inventory: opening + purchases - ending
        </div>
        {missingText ? (
          <div className="text-xs text-amber-700">{missingText}</div>
        ) : null}
        <button
          type="button"
          onClick={() => handleUseInventoryEstimate(item)}
          disabled={!canUse}
          className="mt-1 w-fit rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Use
        </button>
      </div>
    );
  };

  const renderComputedCell = (item, field) => {
    const key = item.item_key
      ? `key:${item.item_key}`
      : `misc:${Number(item.sort_order) || 0}`;
    const computed = computedByKey.get(key);
    if (!computed || !computed[field]) return "--";
    return formatMoney(computed[field]);
  };

  const renderCategoryLabel = (item) => {
    if (item.is_misc) {
      return (
        <input
          type="text"
          value={item.label || ""}
          onChange={(event) => handleLabelChange(item, event.target.value)}
          className={FIELD_CLASS}
          maxLength={60}
          placeholder="Expense label"
        />
      );
    }
    const category = categoryByKey.get(item.item_key);
    const label =
      item.label ||
      buildLabel(category?.gifiCode, category?.gifiTitle, item.item_key);
    return (
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900">{label}</span>
        {category?.tooltip ? (
          <InfoTooltip text={category.tooltip} label={label} />
        ) : null}
      </div>
    );
  };

  const renderWagesSourceToggle = (item) => {
    if (!isWagesItem(item)) return null;
    const source = item.value_source === "payroll" ? "payroll" : "manual";
    return (
      <div className="inline-flex rounded-full border border-slate-300 overflow-hidden">
        <button
          type="button"
          onClick={() => handleWagesSourceChange(item, "manual")}
          className={`px-3 py-1 text-xs md:text-sm font-semibold transition ${source === "manual"
            ? "bg-rose-500 text-white"
            : "text-slate-600 hover:text-slate-900"
            }`}
        >
          Manual
        </button>
        <button
          type="button"
          onClick={() => handleWagesSourceChange(item, "payroll")}
          className={`px-3 py-1 text-xs md:text-sm font-semibold transition ${source === "payroll"
            ? "bg-rose-500 text-white"
            : "text-slate-600 hover:text-slate-900"
            }`}
        >
          Use Payroll
        </button>
      </div>
    );
  };

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Profitability Estimate
            </div>
            <ModalCloseX onClick={handleClose} />
          </div>

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id="profitability-estimate-overview"
              className={getSectionClassName("profitability-estimate-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_profitability_estimate"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="profitability-estimate-filters"
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4 ${getSectionClassName(
                "profitability-estimate-filters"
              )}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className={LABEL_TEXT}>Quick Pick</span>
                <div className="flex flex-wrap items-center gap-2">
                  {QUICK_PICK_OPTIONS.map((option) => {
                    const isActive = quickPick === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => handleQuickPick(option.key)}
                        disabled={contextLoading}
                        className={`${QUICK_PICK_BASE} ${isActive ? QUICK_PICK_ACTIVE : QUICK_PICK_INACTIVE
                          } ${contextLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto] items-end">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <label className={LABEL_TEXT}>Start Date</label>
                    <InfoTooltip
                      text={PROFITABILITY_TOOLTIPS.start_date}
                      label="Start Date"
                    />
                  </div>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <label className={LABEL_TEXT}>End Date</label>
                    <InfoTooltip
                      text={PROFITABILITY_TOOLTIPS.end_date}
                      label="End Date"
                    />
                  </div>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    className={BTN_OUTLINE}
                    disabled={saveLoading}
                  >
                    {saveLoading ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={handleRun}
                    className={BTN_PRIMARY}
                    disabled={runLoading}
                  >
                    {runLoading ? "Running..." : "Run Estimate"}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm md:text-base">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">
                    Sales tax rate used for estimates:
                  </span>
                  <span className="font-semibold text-slate-900">
                    {taxRatePercent}%
                  </span>
                  <InfoTooltip
                    text={PROFITABILITY_TOOLTIPS.tax_rate}
                    label="Sales tax rate"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Revenue in range (Net Sales):</span>
                  <span className="font-semibold text-slate-900">
                    {revenuePreviewLoading
                      ? "..."
                      : revenuePreview !== null && revenuePreview !== undefined
                        ? formatMoney(revenuePreview)
                        : "--"}
                  </span>
                  <InfoTooltip
                    text={PROFITABILITY_TOOLTIPS.revenue}
                    label="Revenue in range"
                  />
                </div>
              </div>
              <div className="text-sm md:text-base text-slate-500">
                $ / month values are prorated to your selected date range.
              </div>
              <div className="text-sm md:text-base text-slate-500">
                Enter monthly totals including sales tax. Use % of revenue for
                costs that scale with sales.
              </div>
              <DateRangeErrorBanner
                show={Boolean(dateRangeError)}
                message={dateRangeError}
              />
              {formError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                  {formError}
                </div>
              )}
              {saveError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                  {saveError}
                </div>
              )}
              {saveSuccess && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm md:text-base text-emerald-700">
                  {saveSuccess}
                </div>
              )}
              {runError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                  {runError}
                </div>
              )}
            </div>

            {configError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {configError}
              </div>
            )}

            {configLoading && (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600 text-sm md:text-base">
                Loading estimate settings...
              </div>
            )}

            {!configLoading && (
              <div
                id="profitability-estimate-details"
                className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(
                  "profitability-estimate-details"
                )}`}
              >
                <div className="px-4 py-3 border-b border-slate-200 text-base md:text-lg font-semibold text-slate-900">
                  Expense Categories
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <SortableTh
                          label="Category"
                          sortKey="category"
                          sortState={
                            categorySortDir
                              ? { key: "category", dir: categorySortDir }
                              : null
                          }
                          onSort={toggleCategorySort}
                          className="px-4 py-3"
                        />
                        <th className="px-4 py-3 font-semibold">
                          <div className="flex items-center gap-1.5">
                            <span>Input mode</span>
                            <InfoTooltip
                              text={PROFITABILITY_TOOLTIPS.input_mode}
                              label="Input mode"
                            />
                          </div>
                        </th>
                        <th className="px-4 py-3 font-semibold">Monthly value</th>
                        <th className="px-4 py-3 font-semibold">
                          <div className="flex items-center gap-1.5">
                            <span>Inventory Estimate</span>
                            <InfoTooltip
                              text="For GIFI 8320 and GIFI 9130 only. Uses inventory activity for the selected date range as a read-only reference."
                              label="Inventory Estimate"
                            />
                          </div>
                        </th>
                        <th className="px-4 py-3 font-semibold text-center align-middle">
                          <div className="flex items-center justify-center gap-1.5">
                            <span>Tax exempt</span>
                            <InfoTooltip
                              text={PROFITABILITY_TOOLTIPS.tax_exempt}
                              label="Tax exempt"
                            />
                          </div>
                        </th>
                        <th className="px-4 py-3 font-semibold text-center align-middle">Pretax</th>
                        <th className="px-4 py-3 font-semibold text-center align-middle">Sales tax</th>
                        <th className="px-4 py-3 font-semibold text-center align-middle">Total</th>
                        <th className="px-4 py-3 font-semibold"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sortedCategoryItems.map(({ item }) => {
                        const payrollActive = isPayrollSource(item);
                        const payrollValue =
                          payrollActive &&
                            computedByKey.get(`key:${WAGES_ITEM_KEY}`)?.total
                            ? computedByKey.get(`key:${WAGES_ITEM_KEY}`)?.total
                            : null;
                        const rowKey = item.is_misc
                          ? `misc:${item.id ?? item.localId}`
                          : item.item_key || item.localId;
                        return (
                          <tr key={rowKey}>
                            <td className="px-4 py-3 min-w-[260px]">
                              {renderCategoryLabel(item)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  {renderModeToggle(item, payrollActive)}
                                  {renderWagesSourceToggle(item)}
                                </div>
                                {payrollActive && (
                                  <div className="text-xs text-slate-500">
                                    Uses Payroll Results -> Total Pay for the selected
                                    date range.
                                  </div>
                                )}
                                {isWagesItem(item) && wagesSourceError && (
                                  <div className="text-xs text-rose-600">
                                    {wagesSourceError}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 min-w-[160px]">
                              {renderValueInput(item, payrollActive, payrollValue)}
                            </td>
                            <td className="px-4 py-3 align-top">
                              {renderInventoryEstimateCell(item)}
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 accent-rose-500"
                                  checked={
                                    payrollActive ? true : Boolean(item.tax_exempt)
                                  }
                                  disabled={payrollActive}
                                  onChange={(event) =>
                                    handleTaxExemptToggle(item, event.target.checked)
                                  }
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              {renderComputedCell(item, "pretax")}
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              {renderComputedCell(item, "sales_tax")}
                            </td>
                            <td className="px-4 py-3 text-center align-middle">
                              {renderComputedCell(item, "total")}
                            </td>
                            <td className="px-4 py-3">
                              {item.is_misc ? (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveMisc(item)}
                                  className="text-rose-600 font-semibold hover:text-rose-700"
                                >
                                  Remove
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!configLoading && (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 text-base md:text-lg font-semibold text-slate-900 flex items-center justify-between gap-3">
                  <span>Other / Miscellaneous</span>
                  <button
                    type="button"
                    onClick={handleAddMisc}
                    className={BTN_OUTLINE}
                  >
                    Add line
                  </button>
                </div>
                <div className="px-4 py-3 text-slate-500 text-sm md:text-base">
                  Add custom expense lines like laundry, contractors, or repairs
                  not covered above. They will appear in the Expense Categories
                  table.
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="text-sm md:text-base text-slate-600">
                  Save your latest Profitability Estimate entries here without scrolling back to the top.
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    className={BTN_OUTLINE}
                    disabled={saveLoading}
                  >
                    {saveLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              {saveError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
                  {saveError}
                </div>
              )}
              {saveSuccess && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm md:text-base text-emerald-700">
                  {saveSuccess}
                </div>
              )}
            </div>

            {runResult && (
              <div
                id="profitability-estimate-kpis"
                className={`grid gap-4 md:grid-cols-4 ${getSectionClassName(
                  "profitability-estimate-kpis"
                )}`}
              >
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Revenue (range)
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(runResult.revenue_in_range)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Estimated expenses
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(runResult.totals?.expenses_total)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Estimated profit
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(runResult.totals?.profit)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Profit margin
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatPercentValue(runResult.totals?.profit_margin_pct)}
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 text-base md:text-lg font-semibold text-slate-900">
                Breakdown
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <SortableTh
                        label="Category"
                        sortKey="category"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3"
                      />
                      <SortableTh
                        label="Input mode"
                        sortKey="input_mode"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3 text-center align-middle"
                        align="center"
                      />
                      <SortableTh
                        label="Monthly value"
                        sortKey="value"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3 text-center align-middle"
                        align="center"
                      />
                      <SortableTh
                        label="Tax exempt"
                        sortKey="tax_exempt"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3 text-center align-middle"
                        align="center"
                      />
                      <SortableTh
                        label="Prorated pretax"
                        sortKey="pretax"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3 text-center align-middle"
                        align="center"
                      />
                      <SortableTh
                        label="Sales tax"
                        sortKey="sales_tax"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3 text-center align-middle"
                        align="center"
                      />
                      <SortableTh
                        label="Prorated total"
                        sortKey="total"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3 text-center align-middle"
                        align="center"
                        buttonClassName="w-full justify-center"
                        contentClassName="justify-center w-full"
                      />
                      <SortableTh
                        label="% of revenue"
                        sortKey="pct_of_revenue"
                        sortState={breakdownSort.sortState}
                        onSort={breakdownSort.handleSort}
                        className="px-4 py-3 text-center align-middle"
                        align="center"
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {breakdownSort.sortedRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-4 text-slate-500 text-sm md:text-base"
                        >
                          Run the estimate to see the breakdown table.
                        </td>
                      </tr>
                    )}
                    {breakdownSort.sortedRows.map((row, idx) => (
                      <tr key={`${row.item_key || row.label}-${idx}`}>
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          {row.label}
                        </td>
                        <td className="px-4 py-3 text-center align-middle">
                          {row.input_mode === "percent" ? "Percent" : "Fixed"}
                        </td>
                        <td className="px-4 py-3 text-center align-middle">
                          {row.input_mode === "percent"
                            ? `${row.value}%`
                            : formatMoney(row.value)}
                        </td>
                        <td className="px-4 py-3 text-center align-middle">
                          {row.tax_exempt ? "Yes" : "No"}
                        </td>
                        <td className="px-4 py-3 text-center align-middle">
                          {formatMoney(row.pretax)}
                        </td>
                        <td className="px-4 py-3 text-center align-middle">
                          {formatMoney(row.sales_tax)}
                        </td>
                        <td className="px-4 py-3 text-center align-middle font-semibold text-slate-900">
                          {formatMoney(row.total)}
                        </td>
                        <td className="px-4 py-3 text-center align-middle font-semibold text-slate-900">
                          {row.pct_of_revenue === null ||
                            row.pct_of_revenue === undefined
                            ? "—"
                            : Number.isFinite(Number(row.pct_of_revenue))
                              ? `${Number(row.pct_of_revenue).toFixed(2)}%`
                              : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              id="profitability-estimate-export"
              className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                "profitability-estimate-export"
              )}`}
            >
              Export is not available on this report.
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm md:text-base text-slate-600 space-y-1">
              <div>
                This is an estimate. Replace % assumptions with actuals as you learn.
              </div>
              <div>Tax exempt means no sales tax is applied to that expense line.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
